import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { en } from './en'
import { vi } from './vi'
import type { Dict } from './en'
import {
  formatDate as fmtDate,
  formatNumber as fmtNumber,
  formatPrice as fmtPrice,
  formatPriceShort as fmtPriceShort,
  timeAgo as fmtTimeAgo,
  type Locale,
} from '@/lib/format'

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  LANGUAGES — the translation layer of this site                          ║
 * ╠══════════════════════════════════════════════════════════════════════════╣
 * ║  ADDING A LANGUAGE IS 3 STEPS AND TOUCHES NO COMPONENT:                   ║
 * ║    1. Copy `src/i18n/en.ts` to `src/i18n/<code>.ts` and translate it.     ║
 * ║       The `Dict` type fails the build if a key is missing.                ║
 * ║    2. Add ONE entry to `registry` below (code, native name, locale).      ║
 * ║    3. (Optional) add a `<field>_<code>` column for data that needs        ║
 * ║       translating, e.g. `title_fr`; `tf()` picks it up automatically.     ║
 * ║                                                                          ║
 * ║  HOUSE RULES:                                                            ║
 * ║  • English is both the DEFAULT and the FALLBACK: an untranslated key      ║
 * ║    shows English rather than a raw technical key.                        ║
 * ║  • Never write display text inside a component — always go through `t()`. ║
 * ║  • Brand copy (site.config.ts) is written as { en, vi } and read with     ║
 * ║    `tx()`; database rows translate via extra columns, read with `tf()`.   ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */

/** Fallback language — used whenever a key is missing in the chosen one. */
export const FALLBACK_LANG = 'en'

interface LanguageEntry {
  /** The language's name in itself — what the picker shows. */
  label: string
  /** Two-letter mark for the header control. */
  short: string
  /** Locale used to format numbers, prices and dates. */
  locale: Locale
  dict: Dict
}

/**
 * LANGUAGE REGISTRY — the single source of truth. The `Lang` type, the picker
 * menu and browser detection are all derived from this object, so a new
 * language is genuinely one line here plus one dictionary file.
 *
 * Ready for more: fr, ja, zh, de… just add `fr: { label: 'Français', short:
 * 'FR', locale: 'fr-FR', dict: fr }` once `src/i18n/fr.ts` exists.
 */
const registry = {
  en: { label: 'English', short: 'EN', locale: 'en-US', dict: en },
  vi: { label: 'Tiếng Việt', short: 'VI', locale: 'vi-VN', dict: vi },
} satisfies Record<string, LanguageEntry>

export type Lang = keyof typeof registry

export const SUPPORTED_LANGS = Object.keys(registry) as Lang[]

/** Rows for the language picker. */
export const LANGUAGES = SUPPORTED_LANGS.map((code) => ({
  code,
  label: registry[code].label,
  short: registry[code].short,
  locale: registry[code].locale,
}))

export function isSupportedLang(value: unknown): value is Lang {
  return typeof value === 'string' && (SUPPORTED_LANGS as string[]).includes(value)
}

const STORAGE_KEY = 'upbo-lang'

/**
 * The language business data is WRITTEN IN (watch titles, descriptions… stored
 * in the un-suffixed column). The boutique writes English; Vietnamese lives in
 * `<field>_vi`. `tf()` uses this constant to know when the base column already
 * IS the translation being asked for.
 */
export const CONTENT_BASE_LANG: Lang = 'en'

/**
 * Which language a first-time visitor gets. The brief is explicit: English by
 * default, everywhere. Flip AUTO_DETECT_BROWSER_LANG to true to greet visitors
 * in their browser language instead (only when it is one we actually support).
 * A saved choice always wins over both.
 */
export const DEFAULT_LANG: Lang = 'en'
const AUTO_DETECT_BROWSER_LANG = false

/**
 * A multi-language string, used throughout `site.config.ts`. English is
 * required (it is the fallback); every other language is optional, so adding a
 * language never breaks a config that is already running.
 */
export type L10n = Record<typeof FALLBACK_LANG, string> & Partial<Record<Lang, string>>

interface I18nValue {
  lang: Lang
  locale: Locale
  setLang: (lang: Lang) => void
  languages: typeof LANGUAGES
  /** Translate by key: t('home.featured'), t('catalog.count', { count: 12 }) */
  t: (path: string, params?: Record<string, string | number>) => string
  /** Read a multi-language config string: tx(siteConfig.hero.title) */
  tx: (value: L10n | string | undefined) => string
  /** Read a data field with optional translation: tf(watch, 'title') → title_vi in VI */
  tf: (row: unknown, field: string) => string
  formatPrice: (v: number) => string
  formatPriceShort: (v: number) => string
  formatNumber: (v: number) => string
  formatDate: (iso: string) => string
  timeAgo: (iso: string) => string
}

const I18nContext = createContext<I18nValue | null>(null)

/**
 * Startup language: a saved choice first, then the browser/system language
 * if supported (e.g. 'vi', 'en'), otherwise fallback to English ('en').
 */
export function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (isSupportedLang(saved)) return saved
  } catch {
    /* storage blocked by the browser */
  }
  try {
    const candidates = [...(navigator.languages ?? []), navigator.language].filter(Boolean)
    for (const candidate of candidates) {
      const base = candidate.slice(0, 2).toLowerCase()
      if (isSupportedLang(base)) return base
    }
  } catch {
    /* no navigator in this environment */
  }
  return FALLBACK_LANG
}

/** Read "a.b.c" out of a dictionary; undefined lets the caller fall back. */
function lookup(dict: Dict, path: string): string | undefined {
  const value = path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key]
    }
    return undefined
  }, dict)
  return typeof value === 'string' ? value : undefined
}

export function I18nProvider({ children, defaultLang }: { children: ReactNode; defaultLang?: Lang }) {
  const [lang, setLangState] = useState<Lang>(() => defaultLang ?? detectLang())

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang)
  }, [lang])

  const setLang = useCallback((next: Lang) => {
    if (!isSupportedLang(next)) return
    setLangState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* storage blocked — the choice simply won't persist */
    }
  }, [])

  const value = useMemo<I18nValue>(() => {
    const entry = registry[lang] ?? registry[FALLBACK_LANG]
    const locale = entry.locale as Locale

    const t = (path: string, params?: Record<string, string | number>) => {
      // Missing in the chosen language → English; missing there too → the key
      // itself, so a forgotten translation is obvious on screen.
      let out = lookup(entry.dict, path) ?? lookup(registry[FALLBACK_LANG].dict, path) ?? path
      if (params) {
        for (const [key, val] of Object.entries(params)) {
          out = out.split(`{${key}}`).join(String(val))
        }
      }
      return out
    }

    return {
      lang,
      locale,
      setLang,
      languages: LANGUAGES,
      t,
      tx: (v) => {
        if (v == null) return ''
        if (typeof v === 'string') return v
        return v[lang] ?? v[FALLBACK_LANG] ?? Object.values(v).find((x) => typeof x === 'string') ?? ''
      },
      tf: (row, field) => {
        const r = (row ?? {}) as Record<string, unknown>
        // Order: the exact translation → the base column (when we're already in
        // the language the data is written in) → English → the base column.
        const chain = lang === CONTENT_BASE_LANG
          ? [`${field}_${lang}`, field, `${field}_${FALLBACK_LANG}`]
          : [`${field}_${lang}`, `${field}_${FALLBACK_LANG}`, field]
        for (const key of chain) {
          const candidate = r[key]
          if (typeof candidate === 'string' && candidate.trim()) return candidate
        }
        return ''
      },
      formatPrice: (v) => fmtPrice(v, locale),
      formatPriceShort: (v) => fmtPriceShort(v, locale),
      formatNumber: (v) => fmtNumber(v, locale),
      formatDate: (iso) => fmtDate(iso, locale),
      timeAgo: (iso) => fmtTimeAgo(iso, locale),
    }
  }, [lang, setLang])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>')
  return ctx
}
